import { memo } from 'react'
import { MessageList } from '../components/MessageList'
import SharedDiceRoll from '../components/SharedDiceRoll'
import type { SharedDiceRollingState } from '../GameControls/useDiceControls'
import type { Message, PrivateMessage } from '@/types/message'
import type { Character } from '@/types'
import type { Member as MemberType } from '@/types/member'

interface PrivateChatProps {
  messages: Message[] | PrivateMessage[]
  characters: Character[]
  members: MemberType[]
  currentUserId: string
  selectedPrivateUser: MemberType | null
  sharedDiceRolling: SharedDiceRollingState
  /** A bandeja unica esta na tela: a cerimonia de dados nao aparece. */
  cerimoniaSuprimida?: boolean
  /** Envio da rolagem congelada na cerimonia (so quem rolou ve o botao). */
  onEnviarRolagem?: (rollId: string) => Promise<boolean>
  loadingHistory: boolean
}

export const PrivateChat = memo(function PrivateChat({
  messages,
  characters,
  members,
  currentUserId,
  selectedPrivateUser,
  sharedDiceRolling,
  cerimoniaSuprimida = false,
  onEnviarRolagem,
  loadingHistory
}: PrivateChatProps) {
  // Render UMA única MessageList — durante rolagem de dados aplicamos blur
  // via classe e sobrepomos o overlay. Antes havia duas instâncias no
  // ternário e cada toggle desmontava/montava o componente, perdendo a
  // posição de scroll (mesmo fix já aplicado ao PublicChat).
  // Quem ROLOU ve a jogada na propria bandeja, onde montou os dados — a
  // cerimonia e para quem assiste. Sem isto, duas telas mostrariam a mesma
  // coisa para a mesma pessoa (decisao do Paulo, 2026-07-25).
  const isRolling = sharedDiceRolling.isActive
    && sharedDiceRolling.userId !== currentUserId
    // Com a bandeja unica aberta (fase A), a jogada acontece NELA para todos
    // — a cerimonia nao pula por cima. O sorteio de personagem nao e jogada
    // de dado e mantem a cerimonia sempre.
    && (!cerimoniaSuprimida || sharedDiceRolling.diceType === 'sorteio')

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
      <MessageList
        messages={messages}
        currentUserId={currentUserId}
        chatMode="private"
        className={
          isRolling
            ? 'flex-1 min-h-0 blur-sm opacity-30 transition-[filter,opacity] duration-200'
            : 'flex-1 min-h-0 transition-[filter,opacity] duration-200'
        }
        characters={characters}
        members={members}
        isLoading={loadingHistory}
        privateConversationId={selectedPrivateUser?.userId}
      />

      {isRolling && (
        <SharedDiceRoll
          isActive={sharedDiceRolling.isActive}
          player={sharedDiceRolling.player}
          diceType={sharedDiceRolling.diceType}
          startTime={sharedDiceRolling.startTime}
          userId={sharedDiceRolling.userId}
          characterName={sharedDiceRolling.characterName}
          result={sharedDiceRolling.result}
          // A jogada inteira tambem aqui: a cerimonia e a mesma, esteja o
          // jogador no chat do grupo ou num privado.
          rollId={sharedDiceRolling.rollId}
          diceSpec={sharedDiceRolling.diceSpec}
          kept={sharedDiceRolling.kept}
          rule={sharedDiceRolling.rule}
          expression={sharedDiceRolling.expression}
          modifiers={sharedDiceRolling.modifiers}
          attributeName={sharedDiceRolling.attributeName}
          attributeValue={sharedDiceRolling.attributeValue}
          bonus={sharedDiceRolling.bonus}
          total={sharedDiceRolling.total}
          difficulty={sharedDiceRolling.difficulty}
          outcome={sharedDiceRolling.outcome}
          souEuQuemRolou={sharedDiceRolling.userId === currentUserId}
          onEnviar={onEnviarRolagem}
          className="absolute inset-0 z-10"
        />
      )}
    </div>
  )
})
